import Remove from "@mui/icons-material/Remove";
import { Tooltip, Typography, type SxProps, type Theme } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { DataGrid } from "components/DataGrid";
import { HoverAction } from "components/HoverAction";
import { useMemo } from "react";
import { NoRowsOverlay } from "routes/Customer/Chemicals/Inventory/Facility/NoRowsOverlay";

import type { SuggestedFacility } from "../types";

type DocumentFacilityRow = SuggestedFacility;

type DocumentFacilitiesGridProps = {
  hideHeader?: boolean;
  noRowsButtonProps?: {
    text?: string;
    buttonText?: string;
    onClick?: () => void;
  };
  facilities: SuggestedFacility[];
  onRemoveFacility?: (facilityId: string) => void;
  loading?: boolean;
  nameCellRenderer?: (row: DocumentFacilityRow) => React.ReactNode;
  addressCellRenderer?: (row: DocumentFacilityRow) => React.ReactNode;
  additionalActionColumns?: GridColDef<DocumentFacilityRow>[];
  sx?: SxProps<Theme>;
  noRowsMessage?: string;
};

function formatAddress({ streetAddress1, city, state, zip }: SuggestedFacility): string {
  return [streetAddress1, city, state, zip].filter(Boolean).join(", ");
}

export function DocumentFacilitiesGrid({
  hideHeader = false,
  noRowsButtonProps,
  facilities,
  onRemoveFacility,
  loading = false,
  nameCellRenderer,
  addressCellRenderer,
  additionalActionColumns = [],
  sx,
  noRowsMessage = "No associated facilities yet.",
}: DocumentFacilitiesGridProps) {
  const rows = useMemo<DocumentFacilityRow[]>(() => {
    return facilities;
  }, [facilities]);

  const columns: GridColDef<DocumentFacilityRow>[] = useMemo(() => {
    const baseColumns: GridColDef<DocumentFacilityRow>[] = [
      {
        field: "name",
        headerName: "Facility Name",
        flex: 1.5,
        renderCell: ({ row }) =>
          nameCellRenderer ? (
            nameCellRenderer(row)
          ) : (
            <Typography variant="body2">{row.name}</Typography>
          ),
      },
      {
        field: "address",
        headerName: "Address",
        flex: 1.5,
        valueGetter: (_, row) => formatAddress(row),
        renderCell: ({ row }) =>
          addressCellRenderer ? (
            addressCellRenderer(row)
          ) : (
            <Typography variant="body2">{formatAddress(row)}</Typography>
          ),
      },
    ];

    // Add additional action columns before the remove action
    const actionColumns: GridColDef<DocumentFacilityRow>[] = [
      ...additionalActionColumns,
      ...(onRemoveFacility
        ? [
            {
              field: "actions",
              type: "actions" as const,
              sortable: false,
              filterable: false,
              flex: 0.1,
              getActions: ({ row }: { row: DocumentFacilityRow }) => {
                return [
                  <Tooltip title="Remove" key="remove">
                    <HoverAction
                      label="Remove"
                      icon={<Remove fontSize="small" color="primary" />}
                      onClick={() => onRemoveFacility(row.id)}
                    />
                  </Tooltip>,
                ];
              },
            },
          ]
        : []),
    ];

    return [...baseColumns, ...actionColumns];
  }, [nameCellRenderer, addressCellRenderer, additionalActionColumns, onRemoveFacility]);

  return (
    <DataGrid
      rows={rows}
      columns={columns}
      loading={loading}
      isRowSelectable={() => false}
      slots={{
        noRowsOverlay: () => (
          <NoRowsOverlay
            message={noRowsButtonProps?.text ?? ""}
            buttonText={noRowsButtonProps?.buttonText}
            onButtonClick={noRowsButtonProps?.onClick ?? (() => {})}
          />
        ),
      }}
      hideFooter
      hideFooterPagination
      disableColumnMenu
      autoHeight
      getRowId={(row) => row.id}
      noRowsMessage={noRowsMessage}
      sx={{
        "& .MuiDataGrid-columnHeaders": {
          borderBottom: "none",
          display: hideHeader ? "none" : undefined,
        },
        "& .MuiDataGrid-columnSeparator": {
          display: hideHeader ? "none" : undefined,
        },
        "& .MuiDataGrid-cell": {
          display: "flex",
          alignItems: "center",
          borderBottom: "none",
        },
        "& .MuiDataGrid-cellContent": {
          display: "flex",
          alignItems: "center",
          height: "100%",
          width: "100%",
        },
        mb: 2,
        ...sx,
      }}
    />
  );
}
